import type { CallerContextMatch } from '@/features/internal/zoom/caller-context-match'
import { contactRecordId } from '@/features/internal/zoom/incoming-phone-response'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface IncomingCallData {
  engagementId: string
  from: string
  to?: string
  queueName?: string
  channel?: string
  customerName?: string
  customerEmail?: string
}

export interface ZoomWidgetPosition {
  x: number
  y: number
}

export interface CallDetails {
  remotePhoneNumber?: string
  remoteCallerName?: string
  callStartTime?: number
  callType?: string
}

export interface InteractionCallLogDetails {
  objectRecord: {
    callType: string
    from: string
    to: string
    callDuration: string
    callStartTime: string
    callEndTime: string
    callQueue?: string
    agentExtension?: string
    agentName?: string
    wrapUpTimeDuration?: string
    notes?: string[]
    dispositionCode?: string
    dispositionId?: string
    type: string
  }
  engagementId: string
  relateTo?: {
    entity: string
    valueNameKey: string
  }
}

/**
 * What Mia AI knew about the caller, looked up once at ring time. Keyed by
 * engagement so a previous call's context can never show against this one.
 */
export interface MatchedCallerContext {
  engagementId: string
  /** The number the lookup asked about, so a refresh asks the same question. */
  phone: string
  /** The server's ring instant, replayed on refresh so the window cannot drift. */
  ringingAtMs: number
  matches: CallerContextMatch[]
  /** Which record the agent picked in Zoom's chooser; only asked when ambiguous. */
  chosenRecordId: string | null
}

export interface UnassignedInteraction {
  sys_id: string
  number: string
  callLogDetails: InteractionCallLogDetails
  createdAt: string
}

interface ZoomWidgetState {
  isVisible: boolean
  isMinimized: boolean
  position: ZoomWidgetPosition
  widgetDimensions: {
    width: number
    height: number
  }
  incomingCall: IncomingCallData | null
  isIncomingCallDialogOpen: boolean
  isZoomLoginInitialized: boolean
  isInCall: boolean
  callDetails: CallDetails | null
  callerContext: MatchedCallerContext | null
  callerContextDismissedFor: string | null
  callLogDetails: InteractionCallLogDetails | null
  showInteractionPanel: boolean
  isCreatingInteraction: boolean
  unassignedInteractions: UnassignedInteraction[]
  editingUnassignedInteractionId: string | null
  currentCallInteractionId: string | null
  currentCallEngagementId: string | null
  currentCallLinkedToCaseId: string | null
  currentCallLinkedToCaseNumber: string | null
  pendingCallEndData: InteractionCallLogDetails | null
  pendingInteractionIdToPatch: string | null
  pendingRecording: PendingRecording | null
}

/**
 * A recording URL together with the engagement it belongs to.
 *
 * The engagement is not decoration — it is the whole point. This used to be a
 * bare `recordingUrl: string`, and the attach effect patched it onto whatever
 * `currentCallInteractionId` happened to hold at the time. Recordings arrive
 * after a call ends, so on back-to-back calls the recording for call N landed
 * on call N+1's interaction: 11,492 prod rows ended up carrying another call's
 * recording URL. Carrying the engagement makes the recording self-describing,
 * so it can only ever be attached to the call it came from.
 */
export interface PendingRecording {
  url: string
  engagementId: string | null
}

const initialState: ZoomWidgetState = {
  isVisible: false,
  isMinimized: false,
  position: {
    x: 20,
    y: 80
  },
  widgetDimensions: {
    width: 500,
    height: 600
  },
  incomingCall: null,
  isIncomingCallDialogOpen: false,
  isZoomLoginInitialized: false,
  isInCall: false,
  callDetails: null,
  callerContext: null,
  callerContextDismissedFor: null,
  callLogDetails: null,
  showInteractionPanel: false,
  isCreatingInteraction: false,
  unassignedInteractions: [],
  editingUnassignedInteractionId: null,
  currentCallInteractionId: null,
  currentCallEngagementId: null,
  currentCallLinkedToCaseId: null,
  currentCallLinkedToCaseNumber: null,
  pendingCallEndData: null,
  pendingInteractionIdToPatch: null,
  pendingRecording: null
}

const zoomWidgetSlice = createSlice({
  name: 'zoomWidget',
  initialState,
  reducers: {
    setWidgetVisible: (state, action: PayloadAction<boolean>) => {
      state.isVisible = action.payload
    },
    setWidgetMinimized: (state, action: PayloadAction<boolean>) => {
      state.isMinimized = action.payload
    },
    setWidgetPosition: (state, action: PayloadAction<ZoomWidgetPosition>) => {
      state.position = action.payload
    },
    setWidgetDimensions: (
      state,
      action: PayloadAction<{ width: number; height: number }>
    ) => {
      state.widgetDimensions = action.payload
    },
    setIncomingCall: (
      state,
      action: PayloadAction<IncomingCallData | null>
    ) => {
      state.incomingCall = action.payload
      if (action.payload) {
        state.isIncomingCallDialogOpen = true
      }
    },
    setIncomingCallDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isIncomingCallDialogOpen = action.payload
    },
    clearIncomingCall: (state) => {
      state.incomingCall = null
      state.isIncomingCallDialogOpen = false
    },
    setZoomLoginInitialized: (state, action: PayloadAction<boolean>) => {
      state.isZoomLoginInitialized = action.payload
      if (action.payload) {
        state.isVisible = true
      }
    },
    setInCall: (state, action: PayloadAction<boolean>) => {
      state.isInCall = action.payload
    },
    setCallDetails: (state, action: PayloadAction<CallDetails>) => {
      state.callDetails = action.payload
    },
    clearCallDetails: (state) => {
      state.callDetails = null
    },
    setCallerContext: (
      state,
      action: PayloadAction<MatchedCallerContext | null>
    ) => {
      state.callerContext = action.payload
    },
    // Hides the panel only: the call is still filed against Mia's case.
    dismissCallerContext: (state) => {
      state.callerContextDismissedFor =
        state.callerContext?.engagementId ?? null
    },
    setCallerContextChoice: (state, action: PayloadAction<string>) => {
      if (state.callerContext)
        state.callerContext.chosenRecordId = action.payload
    },
    // No-ops once the call ends, so a poll in flight cannot revive it.
    setCallerContextMatches: (
      state,
      action: PayloadAction<CallerContextMatch[]>
    ) => {
      if (state.callerContext) state.callerContext.matches = action.payload
    },
    // Interaction creation actions
    setCallLogDetails: (
      state,
      action: PayloadAction<InteractionCallLogDetails | null>
    ) => {
      state.callLogDetails = action.payload
    },
    setShowInteractionPanel: (state, action: PayloadAction<boolean>) => {
      state.showInteractionPanel = action.payload
    },
    setIsCreatingInteraction: (state, action: PayloadAction<boolean>) => {
      state.isCreatingInteraction = action.payload
    },
    addUnassignedInteraction: (
      state,
      action: PayloadAction<UnassignedInteraction>
    ) => {
      state.unassignedInteractions.push(action.payload)
    },

    clearUnassignedInteractions: (state) => {
      state.unassignedInteractions = []
    },
    updateUnassignedInteraction: (
      state,
      action: PayloadAction<UnassignedInteraction>
    ) => {
      const index = state.unassignedInteractions.findIndex(
        (i) => i.sys_id === action.payload.sys_id
      )
      if (index !== -1) {
        state.unassignedInteractions[index] = action.payload
      }
    },
    removeUnassignedInteraction: (state, action: PayloadAction<string>) => {
      state.unassignedInteractions = state.unassignedInteractions.filter(
        (interaction) => interaction.sys_id !== action.payload
      )
    },
    setUnassignedInteractions: (
      state,
      action: PayloadAction<UnassignedInteraction[]>
    ) => {
      state.unassignedInteractions = action.payload
    },
    setEditingUnassignedInteractionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.editingUnassignedInteractionId = action.payload
    },
    setCurrentCallInteractionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.currentCallInteractionId = action.payload
    },
    setCurrentCallEngagementId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      // A linked case belongs to one engagement; nothing else reliably clears
      // it, so a later call would file against the previous caller's case.
      if (action.payload !== state.currentCallEngagementId) {
        state.currentCallLinkedToCaseId = null
        state.currentCallLinkedToCaseNumber = null
      }
      state.currentCallEngagementId = action.payload
    },
    setCurrentCallLinkedToCaseId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.currentCallLinkedToCaseId = action.payload
      if (!action.payload) {
        state.currentCallLinkedToCaseNumber = null
      }
    },
    setCurrentCallLinkedToCaseNumber: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.currentCallLinkedToCaseNumber = action.payload
    },
    setPendingCallEndData: (
      state,
      action: PayloadAction<InteractionCallLogDetails | null>
    ) => {
      state.pendingCallEndData = action.payload
    },
    setPendingInteractionIdToPatch: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.pendingInteractionIdToPatch = action.payload
    },
    clearPendingCallEndData: (state) => {
      state.pendingCallEndData = null
      state.pendingInteractionIdToPatch = null
    },
    setPendingRecording: (
      state,
      action: PayloadAction<PendingRecording | null>
    ) => {
      state.pendingRecording = action.payload
    },
    resetZoomWidgetState: () => {
      return initialState
    }
  }
})

export const {
  setWidgetVisible,
  setWidgetMinimized,
  setWidgetPosition,
  setWidgetDimensions,
  setIncomingCall,
  setIncomingCallDialogOpen,
  clearIncomingCall,
  setZoomLoginInitialized,
  setInCall,
  setCallDetails,
  clearCallDetails,
  setCallerContext,
  dismissCallerContext,
  setCallerContextChoice,
  setCallerContextMatches,
  setCallLogDetails,
  setShowInteractionPanel,
  setIsCreatingInteraction,
  addUnassignedInteraction,
  updateUnassignedInteraction,
  removeUnassignedInteraction,
  clearUnassignedInteractions,
  setUnassignedInteractions,
  setEditingUnassignedInteractionId,
  setCurrentCallInteractionId,
  setCurrentCallEngagementId,
  setCurrentCallLinkedToCaseId,
  setCurrentCallLinkedToCaseNumber,
  setPendingCallEndData,
  setPendingInteractionIdToPatch,
  clearPendingCallEndData,
  setPendingRecording,
  resetZoomWidgetState
} = zoomWidgetSlice.actions

export const zoomWidgetReducer = zoomWidgetSlice.reducer

// Selectors
export const selectCallerContext = (state: { zoomWidget: ZoomWidgetState }) =>
  state.zoomWidget.callerContext

/** The transfer the agent is on, or null while several remain unresolved. */
export const selectChosenCallerMatch = (state: {
  zoomWidget: ZoomWidgetState
}): CallerContextMatch | null => {
  const held = state.zoomWidget.callerContext
  if (!held) return null
  if (held.matches.length === 1) return held.matches[0]
  return (
    held.matches.find((m) => contactRecordId(m) === held.chosenRecordId) ?? null
  )
}

/**
 * The chosen transfer, only while it belongs to the connected call. A context
 * whose call-end event never arrived must not file the next call against its case.
 */
export const selectCallerMatchForLiveCall = (state: {
  zoomWidget: ZoomWidgetState
}): CallerContextMatch | null => {
  const held = state.zoomWidget.callerContext
  if (!held || held.engagementId !== state.zoomWidget.currentCallEngagementId)
    return null
  return selectChosenCallerMatch(state)
}

export const selectCallerContextDismissed = (state: {
  zoomWidget: ZoomWidgetState
}) =>
  !!state.zoomWidget.callerContext &&
  state.zoomWidget.callerContext.engagementId ===
    state.zoomWidget.callerContextDismissedFor

export const selectZoomWidgetVisible = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.isVisible

export const selectZoomWidgetMinimized = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.isMinimized

export const selectZoomLoginInitialized = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.isZoomLoginInitialized

export const selectZoomWidgetPosition = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.position

export const selectZoomWidgetDimensions = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.widgetDimensions

export const selectInCall = (state: { zoomWidget: ZoomWidgetState }) =>
  state.zoomWidget.isInCall

export const selectCallDetails = (state: { zoomWidget: ZoomWidgetState }) =>
  state.zoomWidget.callDetails

export const selectCallLogDetails = (state: { zoomWidget: ZoomWidgetState }) =>
  state.zoomWidget.callLogDetails

export const selectShowInteractionPanel = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.showInteractionPanel

export const selectIsCreatingInteraction = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.isCreatingInteraction

export const selectUnassignedInteractions = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.unassignedInteractions

export const selectEditingUnassignedInteractionId = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.editingUnassignedInteractionId

export const selectCurrentCallInteractionId = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.currentCallInteractionId

export const selectCurrentCallEngagementId = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.currentCallEngagementId

export const selectCurrentCallLinkedToCaseId = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.currentCallLinkedToCaseId

export const selectCurrentCallLinkedToCaseNumber = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.currentCallLinkedToCaseNumber

export const selectPendingCallEndData = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.pendingCallEndData

export const selectPendingInteractionIdToPatch = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.pendingInteractionIdToPatch
export const selectPendingRecording = (state: {
  zoomWidget: ZoomWidgetState
}) => state.zoomWidget.pendingRecording
